import React, { useState, useEffect } from 'react';
import { reportService, ReportFilters } from '../../services/reportService';
import { PDFGenerator } from '../../utils/pdfGenerator';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

interface Props {
    filters: ReportFilters;
    onPreview: (url: string, title: string) => void;
}

const InventoryReports: React.FC<Props> = ({ filters, onPreview }) => {
    const [activeTab, setActiveTab] = useState('topSelling');
    const [data, setData] = useState<any>({ topSelling: [], noMovement: [] });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadData();
    }, [filters]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await reportService.getInventoryAnalytics(filters);
            if (res.success) {
                setData(res.data);
            }
        } catch (error) {
            toast.error('Error al cargar datos de rotación de inventario');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        const period = {
            start: new Date(filters.startDate + 'T00:00:00'),
            end: new Date(filters.endDate + 'T23:59:59')
        };
        const gen = new PDFGenerator('p');
        
        let cols: string[] = [];
        let body: any[][] = [];
        let title = '';

        if (activeTab === 'topSelling') {
            title = 'Productos Más Vendidos (Top Rotación)';
            cols = ['Marca', 'Modelo', 'Talla', 'Color', 'Cantidad Vendida'];
            body = data.topSelling.map((r: any) => [r.brand, r.model, r.size, r.color, r.quantity]);
        } else if (activeTab === 'noMovement') {
            title = 'Productos Sin Movimiento';
            cols = ['Fecha Ingreso', 'Días en Stock', 'Marca', 'Modelo', 'Talla', 'Color', 'Stock Restante'];
            body = data.noMovement.map((r: any) => [
                r.fechaIngreso ? format(new Date(r.fechaIngreso), 'dd/MM/yyyy') : '',
                `${r.diasSinVenta} días`,
                r.marca, r.modelo, r.talla, r.color, r.stock
            ]);
        }

        gen.generateTable(cols, body, title, period);
        onPreview(gen.getBlobUrl(), title);
    };

    const tabs = [
        { id: 'topSelling', name: 'Más Vendidos' },
        { id: 'noMovement', name: 'Baja Rotación / Sin Movimiento' }
    ];

    const currentData = data[activeTab] || [];

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                    >
                        {tab.name}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                    <h3 className="font-semibold text-gray-800">Rotación de Inventario</h3>
                    <button 
                        onClick={handleExport} 
                        className="px-5 py-2.5 bg-indigo-600 text-white shadow-md hover:bg-indigo-700 hover:shadow-lg font-bold text-sm flex items-center gap-2 rounded-xl transition-all transform hover:-translate-y-0.5"
                    >
                        <DocumentArrowDownIcon className="w-5 h-5" />
                        Generar PDF Exportable
                    </button>
                </div>
                
                <div className="overflow-x-auto max-h-[500px]">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="animate-spin w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full mb-4"></div>
                            Calculando rotación...
                        </div>
                    ) : currentData.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            No hay datos suficientes de inventario para este filtro.
                        </div>
                    ) : (
                        <table className="w-full text-sm text-left whitespace-nowrap">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-100 sticky top-0 z-10 shadow-sm">
                                {activeTab === 'topSelling' && (
                                    <tr><th className="px-4 py-3">Marca</th><th className="px-4 py-3">Modelo</th><th className="px-4 py-3">Talla</th><th className="px-4 py-3">Color</th><th className="px-4 py-3 text-right">Cant. Vendida (Periodo)</th></tr>
                                )}
                                {activeTab === 'noMovement' && (
                                    <tr><th className="px-4 py-3">Fecha Ingreso</th><th className="px-4 py-3">Días Estancado</th><th className="px-4 py-3">Marca</th><th className="px-4 py-3">Modelo</th><th className="px-4 py-3">Talla</th><th className="px-4 py-3">Color</th><th className="px-4 py-3 text-right">Stock Actual</th></tr>
                                )}
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {currentData.map((row: any, i: number) => (
                                    <tr key={i} className="hover:bg-indigo-50/30 transition-colors">
                                        {activeTab === 'topSelling' && (
                                            <><td className="px-4 py-3 font-semibold text-indigo-900">{row.brand}</td><td className="px-4 py-3 font-medium">{row.model}</td><td className="px-4 py-3 text-gray-600">{row.size}</td><td className="px-4 py-3 text-gray-600">{row.color}</td><td className="px-4 py-3 text-right font-bold text-green-600">{row.quantity}</td></>
                                        )}
                                        {activeTab === 'noMovement' && (
                                            <><td className="px-4 py-3 text-gray-500">{row.fechaIngreso ? format(new Date(row.fechaIngreso), 'dd/MM/yyyy') : '-'}</td><td className="px-4 py-3 font-semibold text-orange-600">{row.diasSinVenta} días</td><td className="px-4 py-3 text-gray-800">{row.marca}</td><td className="px-4 py-3 font-medium">{row.modelo}</td><td className="px-4 py-3 text-gray-600">{row.talla}</td><td className="px-4 py-3 text-gray-600">{row.color}</td><td className="px-4 py-3 text-right font-bold">{row.stock}</td></>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InventoryReports;
